import { useCallback, useEffect, useState } from 'react';
import { deviceLess } from '_constants/DEVICES';
import { RAIL_WIDTH, RAIL_COLLAPSED_WIDTH } from '../Rail/styles';

const STORAGE_KEY = 'navigation:railCollapsed';
const CHANGE_EVENT = 'navigation:railChanged';

const readPersisted = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writePersisted = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Storage may be unavailable (private mode); the in-memory state still works for this tab.
  }
};

// The rail's own CSS shows it from this width up (Rail/styles); below it the mobile chrome takes over.
const RAIL_SHOWN_QUERY = deviceLess.tableView;
const isRailShown = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(RAIL_SHOWN_QUERY).matches);

const useNavigationRail = () => {
  const [collapsed, setCollapsed] = useState(readPersisted);
  const [shown, setShown] = useState(isRailShown);

  useEffect(() => {
    const query = window.matchMedia?.(RAIL_SHOWN_QUERY);
    if (!query) return undefined;
    const sync = () => setShown(query.matches);
    sync();
    // Safari < 14 only has the old listener API.
    if (query.addEventListener) query.addEventListener('change', sync);
    else query.addListener(sync);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', sync);
      else query.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    const sync = () => setCollapsed(readPersisted());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writePersisted(next);
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return next;
    });
  }, []);

  // How much of the screen's left edge the rail covers right now — 0 where it isn't shown. Anything
  // docked beside it (the Advanced Search drawer, a full-screen map) offsets by this, so it follows the
  // rail's own breakpoint rather than the page's 1024px desktop/compact switch.
  const width = shown ? (collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH) : 0;

  return { collapsed, toggle, width };
};

export default useNavigationRail;
